import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0d80d98a"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  id: "graphTitle",
  style: {"text-align":"left","margin-bottom":"3px"}
}
const _hoisted_2 = { style: {"font-size":"21px"} }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", {
  id: "graphAnalysis",
  style: {"width":"100%","height":"50.9vh"}
}, null, -1))
const _hoisted_4 = { id: "chartStats" }
const _hoisted_5 = { style: {"padding-top":"10px","padding-bottom":"7px"} }
const _hoisted_6 = { style: {"padding":"8px","border":"1px solid #ddd"} }
const _hoisted_7 = { style: {"padding":"8px","border":"1px solid #ddd"} }
const _hoisted_8 = { style: {"padding":"8px","border":"1px solid #ddd"} }
const _hoisted_9 = { style: {"padding":"8px","border":"1px solid #ddd"} }
const _hoisted_10 = { style: {"padding":"8px","border":"1px solid #ddd"} }
const _hoisted_11 = { style: {"padding":"8px","border":"1px solid #ddd"} }

import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { ReportFilter } from "@/types/report.type";
import { getPaginatedEstimations } from "@/service/estimation.service";
import { ValidationFilter_2 } from "@/types/estimation.type";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5lang_es_ES from "@amcharts/amcharts5/locales/es_ES";
import { getParameter } from "@/service/parameter.service";
import { Filter } from "@/types/filter.type";
import { getSamplingPointById } from "@/service/samplingpoint.service";
import { rageDateFormat } from "@/utils/date.utils";
import { i18n } from "@/plugins/i18n.plugin";


export default /*#__PURE__*/_defineComponent({
  props: {
    reportFilter: null
  },
  setup(__props: any) {

const props = __props as { reportFilter: ReportFilter }


let chartRoot: am5.Root | undefined;
let chartRequestId = 0;
const parameterStats = ref<
  {
    name: string | undefined;
    unit: string | undefined;
    max: string | number | null;
    min: string | number | null;
    avg: string | number | null;
  }[]
>([]);
const graphTitle = ref("");

async function updateGraphTitle(parameterIds: number[], requestId: number): Promise<string[]> {
  const parametersData = await Promise.all(
    parameterIds.map(async (paramId) => {
      const filterParam: Filter = {
        fields: {
          "parameter.id": {
            operator: "equal",
            value: paramId,
          },
        },
      };

      const response = await getParameter(undefined, undefined, filterParam);

      return response?.data?.[0] || null;
    })
  );

  if (requestId !== chartRequestId) return [];

  const parameterNames = parametersData.map((parameter) => parameter?.name || "-");

  const parameterCodes = parametersData.map((parameter) => parameter?.code || "-");

  graphTitle.value = `${parameterNames.join(" y ")}: ${rageDateFormat(props.reportFilter.date)}`;

  return parameterCodes;
}

function createLegendItemDot(root: am5.Root, name: string, color: am5.Color) {
  let legendItem = am5.Container.new(root, {
    layout: root.horizontalLayout,
    marginBottom: 0,
    centerY: am5.percent(50),
  });

  let colorDot = am5.Circle.new(root, {
    radius: 5,
    fill: color,
    marginRight: 10,
    centerY: am5.percent(50),
  });

  let label = am5.Label.new(root, {
    text: name,
    fontSize: 14,
    marginRight: 10,
    centerY: am5.percent(50),
  });

  legendItem.children.push(colorDot);
  legendItem.children.push(label);
  return legendItem;
}

async function createChart(parameterIds: number[]) {
  const requestId = ++chartRequestId;

  await nextTick();

  if (requestId !== chartRequestId) return;

  const graphElement = document.getElementById("graphAnalysis") || false;
  if (parameterIds.length === 0 && props.reportFilter.date[0] === null) return;
  if (!graphElement) return;

  const parameterCodes = await updateGraphTitle(parameterIds, requestId);

  if (requestId !== chartRequestId) return;

  if (chartRoot) {
    chartRoot.dispose();
    chartRoot = undefined;
  }

  const samplingPoint = await getSamplingPointById(props.reportFilter.samplingPointIds[0]);

  if (requestId !== chartRequestId) return;

  chartRoot = am5.Root.new("graphAnalysis");
  chartRoot.locale = am5lang_es_ES;
  chartRoot.setThemes([am5themes_Animated.new(chartRoot)]);

  const chart = chartRoot.container.children.push(
    am5xy.XYChart.new(chartRoot, {
      panX: true,
      panY: true,
      wheelX: "panX",
      wheelY: "zoomX",
      pinchZoomX: true,
      paddingLeft: 0,
    })
  );

  // Eje x
  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(chartRoot, {
      baseInterval: { timeUnit: "minute", count: 1 },
      renderer: am5xy.AxisRendererX.new(chartRoot, {
        minorGridEnabled: true,
      }),
    })
  );

  // Eje Y izquierdo
  const yAxisLeft = chart.yAxes.push(
    am5xy.ValueAxis.new(chartRoot, {
      renderer: am5xy.AxisRendererY.new(chartRoot, {
        opposite: false,
        stroke: am5.color(0x007acc),
      }),
      min: parameterCodes[0] === "TMOA" ? 0 : undefined,
      max:
        parameterCodes[0] === "TMOA"
          ? samplingPoint.numTmoa && samplingPoint.numTmoa != 0
            ? samplingPoint.numTmoa + 0.5
            : 6.5
          : undefined,
      maxPrecision: 0,
    })
  );

  // Eje Y derecho
  const yAxisRight = chart.yAxes.push(
    am5xy.ValueAxis.new(chartRoot, {
      renderer: am5xy.AxisRendererY.new(chartRoot, {
        opposite: true,
        stroke: am5.color(0x32cd32),
      }),
      min: parameterCodes[1] === "TMOA" ? 0 : undefined,
      max:
        parameterCodes[1] === "TMOA"
          ? samplingPoint.numTmoa && samplingPoint.numTmoa != 0
            ? samplingPoint.numTmoa + 0.5
            : 6.5
          : undefined,
      maxPrecision: 0,
    })
  );

  // desactivar los grids del eje Y derecho
  const yAxisRightRenderer = yAxisRight.get("renderer") as am5xy.AxisRendererY;
  yAxisRightRenderer.grid.template.setAll({
    visible: false,
  });

  // Eje Y izquierdo
  const yAxisLeftRenderer = yAxisLeft.get("renderer") as am5xy.AxisRendererY;
  yAxisLeftRenderer.labels.template.set("fill", am5.color(0x007acc));
  yAxisLeftRenderer.set("stroke", am5.color(0x007acc));

  // Eje Y derecho
  yAxisRightRenderer.labels.template.set("fill", am5.color(0x32cd32));
  yAxisRightRenderer.set("stroke", am5.color(0x32cd32));

  chart.set("scrollbarX", am5.Scrollbar.new(chartRoot, { orientation: "horizontal", height: 25 }));
  chart.set("scrollbarY", am5.Scrollbar.new(chartRoot, { orientation: "vertical", width: 25 }));

  const legend = chart.children.push(
    am5.Legend.new(chartRoot, {
      x: am5.percent(50),
      centerX: am5.percent(50),
      y: am5.percent(98),
      marginTop: 15,
    })
  );

  parameterStats.value = [];

  let legendParams: am5xy.LineSeries[] = [];
  let seriesPromises: Promise<void>[] = [];

  for (const [index, paramId] of parameterIds.entries()) {
    const promise = (async () => {
      const validationFilter = {
        samplingPointId: props.reportFilter.samplingPointIds,
        parameterId: [paramId],
        date: [props.reportFilter.date[0], props.reportFilter.date[1]],
      } as ValidationFilter_2;

      const estimations = await getPaginatedEstimations(validationFilter);
      if (requestId !== chartRequestId) return;
      const data = estimations.map((estimation) => ({
        date: estimation.dateTime ? new Date(estimation.dateTime.toString()).getTime() : null,
        value: estimation.value,
      }));

      const values = data
        .map((item) => item.value)
        .filter((value): value is number => value !== undefined);

      const maxVal = values.length > 0 ? Math.max(...values) : null;
      const minVal = values.length > 0 ? Math.min(...values) : null;
      const avgVal =
        values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : null;

      const filterParam: Filter = {
        fields: {
          "parameter.id": {
            operator: "equal",
            value: paramId,
          },
        },
      };
      const parameterResponse = await getParameter(undefined, undefined, filterParam);
      if (requestId !== chartRequestId) return;

      const parameter = parameterResponse?.data[0];
      const parameterName = parameter?.name || "Unknown";
      const parameterCode = parameter?.code || "Unknown";

      parameterStats.value.push({
        name: parameterName,
        unit: parameter.unit ? "(" + parameter.unit + ")" : "",
        max: maxVal !== null && maxVal !== undefined ? i18n.global.n(maxVal, "fixed") : "-",
        min: minVal !== null && minVal !== undefined ? i18n.global.n(minVal, "fixed") : "-",
        avg:
          parameterCode === "TMOA" || parameterCode === "ALT"
            ? i18n.global.n(avgVal, "rounded")
            : avgVal !== null && avgVal !== undefined
            ? i18n.global.n(avgVal, "fixed")
            : "-",
      });

      const yAxis = index === 0 ? yAxisLeft : yAxisRight;
      const lineColor = yAxis === yAxisLeft ? am5.color(0x007acc) : am5.color(0x32cd32);

      const series = chart.series.push(
        am5xy.LineSeries.new(chartRoot, {
          name: parameterName,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "value",
          valueXField: "date",
          tooltip: am5.Tooltip.new(chartRoot, {
            labelText: "Valor: {valueY} \n{valueX.formatDate('yyyy-MM-dd HH:mm')}",
          }),
          stroke: lineColor,
        })
      );

      let cursor = chart.set("cursor", am5xy.XYCursor.new(chartRoot, {}));
      cursor.lineX.set("visible", true);
      cursor.lineY.set("visible", false);

      series.strokes.template.set("stroke", lineColor);
      series.data.setAll(data);

      interface DataContext {
        value: number;
      }

      series.bullets.push((root, _, dataItem) => {
        const dataContext = dataItem.dataContext as DataContext | undefined;

        if (dataContext && dataContext.value !== undefined) {
          const value = dataContext.value;
          let circleColor = null;
          let radius = 4;
          if (parameterCode !== "TMOA") {
            if (value === minVal) circleColor = am5.color(0x50c878);
            else if (value === maxVal) circleColor = am5.color(0xffa500);
          }

          if (circleColor) {
            return am5.Bullet.new(root, {
              sprite: am5.Circle.new(root, {
                radius,
                fill: circleColor,
              }),
            });
          }
        }
        return undefined;
      });

      legendParams.push(series);
    })();

    seriesPromises.push(promise);
  }

  await Promise.all(seriesPromises);

  if (requestId !== chartRequestId) return;

  legendParams.forEach((serie) => {
    legend.data.push(serie);
  });

  const minLegendItem = createLegendItemDot(chartRoot, "Mínimo", am5.color(0x50c878));
  legend.children.push(minLegendItem);

  const maxLegendItem = createLegendItemDot(chartRoot, "Máximo", am5.color(0xffa500));
  legend.children.push(maxLegendItem);

  chart.appear(1000, 100);
}

onMounted(() => {
  createChart(props.reportFilter.parameterIds.filter((id) => id !== 0));
});

onUnmounted(() => {
  chartRequestId++;

  if (chartRoot) {
    chartRoot.dispose();
    chartRoot = undefined;
  }
});

watch(
  () => [...props.reportFilter.parameterIds, ...props.reportFilter.date],
  () => {
    if (
      props.reportFilter.parameterIds.length &&
      props.reportFilter.parameterIds[0] !== 0 &&
      props.reportFilter.date[0]
    ) {
      createChart(props.reportFilter.parameterIds.filter((id) => id !== 0));
    }
  },
  { immediate: false }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h3", _hoisted_2, _toDisplayString(graphTitle.value), 1)
    ]),
    _hoisted_3,
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(parameterStats.value, (stats, index) => {
        return (_openBlock(), _createElementBlock("div", { key: index }, [
          _createElementVNode("div", null, [
            _createElementVNode("h3", _hoisted_5, _toDisplayString(stats.name || "N/A") + " " + _toDisplayString(stats.unit), 1)
          ]),
          _createElementVNode("table", null, [
            _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", _hoisted_6, _toDisplayString(_ctx.$t("label.valueMax")), 1),
                _createElementVNode("th", _hoisted_7, _toDisplayString(_ctx.$t("label.valueMin")), 1),
                _createElementVNode("th", _hoisted_8, _toDisplayString(_ctx.$t("label.valueAverage")), 1)
              ])
            ]),
            _createElementVNode("tbody", null, [
              _createElementVNode("tr", null, [
                _createElementVNode("td", _hoisted_9, _toDisplayString(stats.max), 1),
                _createElementVNode("td", _hoisted_10, _toDisplayString(stats.min), 1),
                _createElementVNode("td", _hoisted_11, _toDisplayString(stats.avg), 1)
              ])
            ])
          ])
        ]))
      }), 128))
    ])
  ]))
}
}

})